"use client";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useMemo, useState } from "react";
import makeStyles from "../../theming/make-styles.js";
import InputBox from "./input-box.js";
const useStyles = makeStyles()((theme, { color, direction, variant }) => {
    const palette = theme.palette(color).input[variant];
    return {
        group: {
            flexDirection: direction === "horizontal" ? "row" : "column",
            [`& > :not(:first-child)`]: direction === "vertical"
                ? {
                    "borderTop": `${theme.size(1)} solid ${palette.group.borderColor}`,
                    "borderLeft": 0,
                    "&:not(:first-child)": {
                        borderTopRightRadius: 0,
                        borderTopLeftRadius: 0,
                    },
                    "&:not(:last-child)": {
                        borderBottomRightRadius: 0,
                        borderBottomLeftRadius: 0,
                    },
                }
                : {
                    "borderTop": 0,
                    "borderLeft": `${theme.size(1)} solid ${palette.group.borderColor}`,
                    "&:not(:first-child)": {
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                    },
                    "&:not(:last-child)": {
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                    },
                },
        },
    };
});
export const InputGroupContext = React.createContext({ inputGroup: false });
export const InputGroup = React.forwardRef(function InputGroup(_a, ref) {
    var { block, children, className, color = "gray", direction = "horizontal", disabled, shadow, size = "medium", valid, variant = "outline", onBlur, onFocus } = _a, props = __rest(_a, ["block", "children", "className", "color", "direction", "disabled", "shadow", "size", "valid", "variant", "onBlur", "onFocus"]);
    const [focused, setFocused] = useState(false);
    const { classes, cx } = useStyles({ color, direction, variant });
    const handleBlur = useCallback((child, e) => {
        var _a, _b;
        if ("onBlur" in child.props) {
            onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
            (_b = (_a = child.props).onBlur) === null || _b === void 0 ? void 0 : _b.call(_a, e);
        }
        setFocused(false);
    }, [onBlur]);
    const handleFocus = useCallback((child, e) => {
        var _a, _b;
        if ("onFocus" in child.props) {
            onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
            (_b = (_a = child.props).onFocus) === null || _b === void 0 ? void 0 : _b.call(_a, e);
        }
        setFocused(true);
    }, [onFocus]);
    const onBlurs = useMemo(() => React.Children.map(children, (child) => (e) => !!child && handleBlur(child, e)), [children, handleBlur]);
    const onFocuses = useMemo(() => React.Children.map(children, (child) => (e) => !!child && handleFocus(child, e)), [children, handleFocus]);
    const items = React.Children.map(children, (child, index) => {
        return (!!child &&
            React.cloneElement(child, Object.assign(Object.assign(Object.assign(Object.assign({ block }, child.props), { key: index, color,
                disabled,
                size }), (valid !== undefined && { valid })), { variant, onBlur: onBlurs === null || onBlurs === void 0 ? void 0 : onBlurs[index], onFocus: onFocuses === null || onFocuses === void 0 ? void 0 : onFocuses[index] })));
    });
    return (_jsx(InputGroupContext.Provider, { value: { inputGroup: true }, children: _jsx(InputBox, Object.assign({ ref: ref, "aria-orientation": direction, block: block, className: cx(classes.group, className), color: color, "data-component": "input-group", disabled: disabled, focused: focused, shadow: shadow, valid: valid, variant: variant }, props, { children: items })) }));
});
export default InputGroup;
//# sourceMappingURL=input-group.js.map